import acceptLangParser from 'accept-language-parser'
import micromatch from 'micromatch'
import Negotiator from 'negotiator'
import { type ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers'
import { type NextRequest } from 'next/server'

import type { CountryCode, Lang, LocaleCode, LocaleConfig, LocalePath } from '@src/i18n/locales'
import { i18nConfig } from '@src/i18n/locales'
import { HEADERS } from '@src/utils/constants.util'
import { isExternalBODiLink, transformExternalBODiLink } from '@src/utils/externalBodiLink'
import { applyShopifyMarketsParams } from '@src/utils/externalShopifyLink'
import { log } from '@src/utils/log.util'

/** The URL segment prefix for all streaming (logged-in) routes. */
export const STREAMING_APP_PREFIX = '/app'

export const isAppPath = (path: string) =>
  path === STREAMING_APP_PREFIX || path.startsWith(STREAMING_APP_PREFIX + '/')

/** Strip a leading /app segment so locale helpers work on both /app/us/en/... and /us/en/... paths. */
const stripAppPrefix = (path: string) =>
  isAppPath(path) ? path.slice(STREAMING_APP_PREFIX.length) : path

export function getDefaultLocale() {
  const defaultLocale = i18nConfig.defaultLocale as keyof typeof i18nConfig.locales
  return i18nConfig.locales[defaultLocale] as LocaleConfig
}

export function getLocaleFromHeaders(headers: ReadonlyHeaders) {
  const localeCode = headers.get(HEADERS.locale) ?? getDefaultLocale().code
  return getLocaleFromCode(localeCode)
}

/**
 * Get all locales, optionally filtered by path restrictions.
 *
 * @param options - Optional configuration object
 * @param options.path - The path to check for locale restrictions (can be localized or unlocalized). Defaults to '/'
 * @returns Array of locale configs that are allowed on the specified path
 */
export function getLocales(options?: { path?: string }): LocaleConfig[] {
  const allLocales = Object.values(i18nConfig.locales) as LocaleConfig[]

  // If no path provided, default to '/' (root)
  const path = options?.path ?? '/'
  const unlocalizedPath = getUnlocalizedPath(path)

  // Filter locales by path restrictions
  return allLocales.filter((locale) => isLocaleAllowedOnPath(locale.code, unlocalizedPath))
}

export function getLocaleCodes() {
  return Object.values(i18nConfig.locales).map((locale) => locale.code) as LocaleCode[]
}

export function getLocalePaths() {
  return Object.values(i18nConfig.locales).map((locale) => locale.path) as LocalePath[]
}

export function getLocaleFromCountryCode(countryCode: string) {
  return getLocales().find((locale) => locale.countryCode === countryCode) ?? getDefaultLocale()
}

/**
 * Determine the most appropriate supported locale based on a user's
 * geolocated country and accept-language header sent by their browser.
 */
export function getAppropriateLocale(geoCountry: string, acceptLang: string | null) {
  const country = geoCountry.toUpperCase() as CountryCode

  // Group all locale codes by country code
  const localeCodesByCountry = Object.values(i18nConfig.locales).reduce(
    (acc, locale) => {
      if (!acc[locale.countryCode]) {
        acc[locale.countryCode] = []
      }
      acc[locale.countryCode].push(locale.code)
      return acc
    },
    {} as Record<CountryCode, LocaleCode[]>
  )

  const localeCodes = localeCodesByCountry[country] ?? [i18nConfig.defaultLocale]
  const defaultLocaleCode = i18nConfig.defaultLocaleByCountry[country] ?? i18nConfig.defaultLocale
  const appropriateLocaleCode =
    acceptLangParser.pick(localeCodes, acceptLang ?? defaultLocaleCode) ?? defaultLocaleCode
  const locale = getLocaleFromCode(appropriateLocaleCode) ?? getDefaultLocale()
  return locale
}

/**
 * This function returns the locale for the given `countryCodeLowercase`.
 *
 * If there are multiple locales for the same country code,
 * `lang` will be used to find the matching one.
 *
 * If `lang` is ommited or if there are no matches, the first locale found for the country code will be returned.
 */
export const getLocaleForCountryCodeLang = (countryCodeLowercase: string, lang?: string) => {
  const localesForCountry = getLocales().filter(
    (l) => l.countryCodeLowercase === countryCodeLowercase
  )
  if (localesForCountry.length === 1) return localesForCountry[0]
  return localesForCountry.find((l) => l.lang === lang) ?? localesForCountry[0]
}

// ['en', 'fr']
export function getLangs() {
  const langsSet = new Set(Object.values(i18nConfig.locales).map((locale) => locale.lang))
  return Array.from(langsSet) as Array<Lang>
}

// "en-US" -> LocaleConfig
export const getLocaleFromCode = (localeCode: string) => {
  const locale = Object.values(i18nConfig.locales).find((locale) => locale.code === localeCode)
  return (locale ?? getDefaultLocale()) as LocaleConfig
}

// "/us/en/..." or "/app/us/en/..." -> LocaleConfig
export const getLocaleFromPath = (path: string = getDefaultLocale().path) => {
  const normalizedPath = stripAppPrefix(path)
  const locale = Object.values(i18nConfig.locales).find((locale) => {
    return normalizedPath.startsWith(locale.path)
  })
  return (locale ?? getDefaultLocale()) as LocaleConfig
}

// URL -> LocaleConfig
export function getLocaleFromURL(url: URL) {
  const pathname = url.pathname
  return getLocaleFromPath(pathname)
}

// "en-US" -> "/us/en"
export const getLocalePathFromLocaleISO = (localeCode?: string | null) => {
  if (!localeCode) return ''
  const [lang, country] = localeCode.split(/-|_/)
  return `/${country.toLowerCase()}/${lang.toLowerCase()}`
}

// "en-US" -> {country: 'US', lang: 'en'}
export const getLocalePartsFromLocaleISO = (localeCode?: string | null) => {
  if (!localeCode) return null
  const [lang, country] = localeCode.split(/-|_/)
  return { country: country.toUpperCase(), lang: lang.toLowerCase() }
}

export const getLocaleCodeFromCountryLang = (country: string, lang: string) => {
  const locale = Object.values(i18nConfig.locales).find(
    (locale) => locale.countryCodeLowercase === country && locale.lang === lang
  )
  return locale?.code ?? ''
}

export const getPreferredLanguage = (request: NextRequest) => {
  const headers = { 'accept-language': request.headers.get('accept-language') ?? 'en-US,en;q=0.5' }

  const languages = new Negotiator({
    headers,
  }).languages()

  // languages can be region specific or not as in:
  // [ 'en-US', 'en', 'pt' ]
  const preferredLanguage = languages[0].split('-')[0]

  return preferredLanguage
}

// "/us/en/some/path" -> "/us/en"
export const getPathLocale = (localizedPath: string | null) => {
  if (localizedPath === '/') return getDefaultLocale().path
  const locale = getLocaleFromPath(String(localizedPath))
  return locale.path
}

/** Returns the path without the locale. */
export const getUnlocalizedPath = (localizedPath: string) => {
  if (localizedPath === '/') return localizedPath
  const pathLocale = getPathLocale(localizedPath)
  const unlocalizedPath = localizedPath.replace(pathLocale ?? '', '')
  return unlocalizedPath
}

export function isAppHomepagePath(localizedPath: string) {
  if (!isAppPath(localizedPath)) return false

  const pathWithoutAppPrefix = localizedPath.slice(STREAMING_APP_PREFIX.length)
  const unlocalizedPath = getUnlocalizedPath(pathWithoutAppPrefix)
  return unlocalizedPath === '/' || !unlocalizedPath
}

export const getPathFromUrl = (fullUrl?: string) => {
  if (!fullUrl) return ''
  const url = fullUrl.replace('http://', '').replace('https://', '')

  const firstSlashIndex = url.indexOf('/')

  const path = Array.from(url).slice(firstSlashIndex).join('')

  return path
}

// "/us/en" -> true, "/app/us/en" -> true, "/fr/fr" -> false
export function isValidLocalePath(localePath: string) {
  const normalizedPath = stripAppPrefix(localePath)
  return Object.entries(i18nConfig.locales).some(([_, locale]) => {
    return normalizedPath.startsWith(locale.path)
  })
}

// "/us/en" -> true, "/fr/fr" -> false
export function isValidCountry(localePath: string) {
  const country = localePath.split('/')[1].toLowerCase()
  return Object.entries(i18nConfig.locales).some(([_, locale]) => {
    return locale.countryCodeLowercase === country
  })
}

/**
 * Return the first English value found in a translated field.
 * To be used for analytics, reporting, or any case where we always want to send English.
 *
 * @example
 * **Input:**
 * ```
 * {
 *   "fr-CA": "Energize avant l'entraînement",
 *   "en-CA": "Energize Pre-Workout"
 * }
 * ```
 *
 * **Output:**
 * `'Energize Pre-Workout'`
 */
export function getEnglishFieldValue<T = string>(field: Record<string, T>) {
  try {
    const entries = Object.entries(field)
    const englishField = entries.find(([code]) => code.startsWith('en'))
    return englishField?.[1] ?? null
  } catch {
    return null
  }
}

/**
 * Check if a locale is allowed on a given path
 * @param localeCode - The locale code (e.g., 'en-AU')
 * @param pathname - The full pathname including locale (e.g., '/au/en/faq')
 * @returns true if the locale is allowed on this path
 */
export function isLocaleAllowedOnPath(localeCode: string, pathname: string): boolean {
  const locale = Object.values(i18nConfig.locales).find((l) => l.code === localeCode)

  // If no restrictions, locale is allowed everywhere
  if (!locale?.allowedPathPatterns || locale.allowedPathPatterns.length === 0) {
    return true
  }

  // Get the unlocalized path (remove locale prefix)
  const unlocalizedPath = getUnlocalizedPath(pathname)

  // Check if the path matches any of the allowed patterns
  return micromatch.isMatch(unlocalizedPath, locale.allowedPathPatterns)
}

const isBodiHost = (hostname: string) => hostname === 'bodi.com' || hostname.endsWith('.bodi.com')

/**
 * Prepend the given locale's path to a URL. Already-localized paths and unrelated
 * external URLs are returned unchanged. The root URL ('/') is returned unchanged
 * for the default locale, since '/' is its public-facing home URL. If the
 * resulting path is not allowed for the locale, the locale's fallback path is
 * used instead.
 *
 * For absolute URLs on our own bodi.com domains (including Shopify subdomains),
 * Shopify Markets context params are attached so that any eventual redirect to
 * Shopify honors the locale instead of falling back to canonical English.
 */
export function getLocalizedUrl(url: string, locale: LocaleConfig): string {
  // `new URL(...)` throws on malformed input — the catch returns the URL as-is.
  try {
    // External BODi domains (TBB/BB/BOD) and Shopify hosts: hostname swap,
    // locale query, Markets context — all handled by the transformer.
    if (isExternalBODiLink(url)) return transformExternalBODiLink(url, locale)

    // Other absolute URLs: only own bodi.com hosts get Markets params attached;
    // the params propagate through bodcheckout → /offers → Shopify redirects.
    if (url.startsWith('http')) {
      const parsed = new URL(url)
      if (!isBodiHost(parsed.hostname)) return url
      applyShopifyMarketsParams(parsed, locale)
      return parsed.toString()
    }

    if (isValidLocalePath(url)) return url
    if (url === '/' && locale.code === getDefaultLocale().code) return url

    // For /app/* paths inject the locale after /app (e.g. /app/workouts → /app/us/en/workouts)
    if (isAppPath(url)) {
      const rest = url.slice(STREAMING_APP_PREFIX.length) // e.g. '/workouts'
      const localePath = isLocaleAllowedOnPath(locale.code, `${locale.path}${rest}`)
        ? locale.path
        : getLocaleFromCode(locale.fallbackLocale).path
      return `${STREAMING_APP_PREFIX}${localePath}${rest}`
    }

    const localePath = isLocaleAllowedOnPath(locale.code, `${locale.path}${url}`)
      ? locale.path
      : getLocaleFromCode(locale.fallbackLocale).path

    return `${localePath}${url}`
  } catch (error) {
    log.error('Error in getLocalizedUrl', { error })
    return url
  }
}
